import { useGetBaggageInfoQuery, useGetMiniRuleInfoQuery } from '@/src/api/rtkQuery/srpFlightDetailsApi'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { QueryInput } from '@/src/types/searchQuery'
import { constructSplrt } from '@/src/utils/srp/RT/constructSplRT'
import { getTotalPriceForNewIntlRTT } from '@/src/utils/srp/RT/get-price'
import { getSearchQueryObj } from '@/src/utils/srp/searchUtils'
import { getSRPMiniRuleRequest } from '@/src/utils/srp/searchUtils/getMiniRuleRequest'
import { getSRPBaggageRequest } from '@/src/utils/srp/searchUtils/getSRPBaggageRequest'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'
import useFormatDate from '../../date/useFormatDate'
import { useGetSearchParamsObj } from '../searchParams'
import useSearchResults from '../useSearchResults'

export const useFlightDetails = ({
    flights,
    jsons,
    sectors,
    onSelect,
    sid,
    showBaggage = true,
    isRTSolutionView
}: {
    flights: FlightCard[]
    jsons: JsonsData
    sectors: Sectors
    onSelect: () => void
    sid: string
    showBaggage?: boolean
    isRTSolutionView?: boolean
}) => {
    const { formatDate } = useFormatDate()
    const { urlQueryObj } = useGetSearchParamsObj()
    const t = useTranslations('common')
    const searchQueryObj = getSearchQueryObj(urlQueryObj as QueryInput)
    const { isPerPaxPricingEnabled } = useSearchResults()
    const [retryCount, setRetryCount] = useState(0)
    const MAX_RETRIES = 2
    // Baggage Request
    const baggageRequest = useMemo(() => {
        if (flights.length === 2 && isRTSolutionView) {
            const [onwardDetails, returnDetails] = flights
            const { isSpecialRT: isOnwardSpecialRT, sFare: onwardSFare } =
                constructSplrt(onwardDetails, returnDetails && returnDetails.splRtFn) || {}
            const { isSpecialRT: isReturnSpecialRT, sFare: returnSFare } =
                constructSplrt(returnDetails, onwardDetails && onwardDetails.splRtFn) || {}

            let isSplrtJourney = isOnwardSpecialRT && isReturnSpecialRT

            if (isSplrtJourney) {
                const totalRetailPrice =
                    (onwardDetails?.priceBreakup?.fare?.pr || 0) + (returnDetails?.priceBreakup?.fare?.pr || 0)
                const totalSplrtRtPrice = (onwardSFare?.pr || 0) + (returnSFare?.pr || 0)
                isSplrtJourney = totalSplrtRtPrice > 0 && totalSplrtRtPrice <= totalRetailPrice
            }

            const onwardBaggageRequest = getSRPBaggageRequest(
                onwardDetails,
                searchQueryObj,
                sectors,
                formatDate,
                jsons,
                isSplrtJourney ? onwardSFare : undefined
            )
            const returnBaggageRequest = getSRPBaggageRequest(
                returnDetails,
                searchQueryObj,
                sectors,
                formatDate,
                jsons,
                isSplrtJourney ? returnSFare : undefined
            )
            onwardBaggageRequest.sol.push(returnBaggageRequest.sol[0])
            return onwardBaggageRequest
        } else {
            return getSRPBaggageRequest(flights[0], searchQueryObj, sectors, formatDate, jsons)
        }
    }, [flights, searchQueryObj, sectors, formatDate, jsons])

    // Baggage Data and state
    const {
        data: baggageData,
        error: baggageRTKError,
        isLoading: baggageLoading,
        refetch: refetchBaggageInfo
    } = useGetBaggageInfoQuery(baggageRequest, {
        skip: !showBaggage || !baggageRequest
    })

    // Mini Rule Request
    const miniRuleRequest = useMemo(() => {
        if (flights.length === 2 && isRTSolutionView) {
            const [onwardDetails, returnDetails] = flights
            const {
                isSpecialRT: isOnwardSpecialRT,
                sFare: onwardSFare,
                solutionId: onwardSFareSolutionId
            } = constructSplrt(onwardDetails, returnDetails.splRtFn) || {}

            const {
                isSpecialRT: isReturnSpecialRT,
                sFare: returnSFare,
                solutionId: returnSFareSolutionId
            } = constructSplrt(returnDetails, onwardDetails.splRtFn) || {}

            let isSplrtJourney = isOnwardSpecialRT && isReturnSpecialRT

            if (isSplrtJourney) {
                const totalRetailPrice =
                    (onwardDetails?.priceBreakup?.fare?.pr || 0) + (returnDetails?.priceBreakup?.fare?.pr || 0)
                const totalSplrtRtPrice = (onwardSFare?.pr || 0) + (returnSFare?.pr || 0)
                isSplrtJourney = totalSplrtRtPrice > 0 && totalSplrtRtPrice <= totalRetailPrice
            }

            const onwardMiniRuleRequest = getSRPMiniRuleRequest(
                onwardDetails,
                searchQueryObj,
                sectors,
                formatDate,
                sid,
                jsons,
                isSplrtJourney ? onwardSFareSolutionId : onwardDetails.priceBreakup.fare.solutionId!,
                isSplrtJourney ? onwardSFare : undefined
            )

            const returnMiniRuleRequest = getSRPMiniRuleRequest(
                returnDetails,
                searchQueryObj,
                sectors,
                formatDate,
                sid,
                jsons,
                isSplrtJourney ? returnSFareSolutionId : returnDetails.priceBreakup.fare.solutionId!,
                isSplrtJourney ? returnSFare : undefined
            )

            return { payload: [onwardMiniRuleRequest, returnMiniRuleRequest], isSplrt: !!isSplrtJourney }
        }
        const oneWayMiniRuleRequest = getSRPMiniRuleRequest(
            flights[0],
            searchQueryObj,
            sectors,
            formatDate,
            sid,
            jsons,
            flights?.[0]?.priceBreakup.fare.solutionId!
        )
        return { payload: [oneWayMiniRuleRequest], isSplrt: false }
    }, [flights, searchQueryObj, sectors, formatDate, sid, jsons])

    // Mini Rule Data and state
    const {
        data: minirule,
        isLoading: miniRuleLoading,
        error: miniRuleRTKError,
        refetch: refetchMiniRulesInfo
    } = useGetMiniRuleInfoQuery(miniRuleRequest)

    const prices = useMemo(() => {
        if (isRTSolutionView) {
            if (flights.length === 2) {
                const { totalPriceTwoLegs, totalStrikeOffPriceTwoLegs } = getTotalPriceForNewIntlRTT(flights)
                return {
                    price: totalPriceTwoLegs,
                    strikeOffPrice: totalStrikeOffPriceTwoLegs
                }
            } else {
                return {
                    price: flights[0].priceBreakup.multiAirlinePrice,
                    strikeOffPrice: flights[0].priceBreakup.multiAirlinePriceWoDiscount
                }
            }
        }
        //one way
        return {
            price: isPerPaxPricingEnabled ? flights[0].priceBreakup?.totalAdultFare : flights[0].priceBreakup.pr,
            strikeOffPrice: isPerPaxPricingEnabled
                ? flights[0].priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice
                : flights[0].priceBreakup?.fare?.strike_off_details?.strikedOffPrice
        }
    }, [flights, isRTSolutionView, isPerPaxPricingEnabled])

    const footerButtonCTA = useMemo(() => {
        if (isRTSolutionView) {
            if (flights.length === 2) {
                return t('bookNow')
            }
            return t('Select')
        }
        return t('bookNow')
    }, [isRTSolutionView])

    // Handle retry logic
    const handleRetryBaggage = () => {
        if (retryCount < MAX_RETRIES) {
            setRetryCount(prev => prev + 1)
            refetchBaggageInfo()
        }
    }

    return {
        baggageData,
        baggageRTKError,
        baggageLoading,
        refetchBaggageInfo,
        minirule,
        miniRuleRTKError,
        miniRuleLoading,
        refetchMiniRulesInfo,
        prices,
        footerButtonCTA,
        retryCount,
        handleRetryBaggage,
        maxRetries: MAX_RETRIES
    }
}
